import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ErrorMessage, InfoMessage } from "@msys/ui";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useUserData } from "../../auth/useUserData.js";
import { Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { enqueueSnackbar } from "notistack";
import React from "react";
import {
  PimProductPreferenceScope,
  PimProductTypeSystemKey,
} from "../../../clients/graphqlTypes.js";
import { ButtonCircleWrapper } from "../../commons/button/ButtonCircleWrapper.js";
import { ProductPreferenceButton } from "../../features/products/buttons/ProductPreferenceButton.js";
import { ProductTypeAssignmentSearch } from "../../features/products/components/ProductTypeAssignmentSearch.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../../features/products/Product.generated.js";
import { UseBrandPreferences } from "../../features/products/useBrandPreferences.js";
import { PreferenceKind } from "../../features/products/productPreferences.js";
import { useProductPreferences } from "../../features/products/useProductPreferences.js";
import { useStateWithUrlParam } from "../../commons/hooks/useStateWithUrlParam.js";
import { useProductPreferencesPage_ProductTypesQuery } from "./ProductPreferences.generated.js";
import { RankedPreferenceList } from "./RankedPreferenceList.js";

interface BrandPreferencesProps {
  brandPreferences: UseBrandPreferences;
}

interface Props extends BrandPreferencesProps {
  productTypeKey: string;
  productTypeSystemKey: PimProductTypeSystemKey | null;
  currentProductTypeSystemKey: PimProductTypeSystemKey | null;
}

/**
 * The selected product type's own article and brand preferences as one ranked list, in the
 * same list views the product search offers, with adding articles and brands as the screen's
 * one action.
 */
export function ProductTypePreferences({
  productTypeKey,
  productTypeSystemKey,
  currentProductTypeSystemKey,
  brandPreferences,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);
  const client = useApolloClient();

  const [mode, setMode] = React.useState<"preferences" | "assign">(
    "preferences"
  );
  // In the URL, as list tabs elsewhere keep theirs, so a reload or a shared link reopens it.
  const [kindParam, setKindParam] = useStateWithUrlParam<string>(
    "kind",
    "articles"
  );
  const kind: PreferenceKind = kindParam === "brands" ? "BRANDS" : "ARTICLES";
  const tabs = React.useMemo(
    () => ({
      kind,
      kinds: BOTH_KINDS,
      onChange: (next: PreferenceKind) =>
        setKindParam(next === "BRANDS" ? "brands" : "articles"),
    }),
    [kind, setKindParam]
  );
  const scope = usePreferenceScopeParam();

  // A current-system route carries no system in the URL. Omitting it would let PIM fall back
  // to its own default, which need not be the system the organisation selected.
  const resolvedProductTypeSystemKey =
    productTypeSystemKey ?? currentProductTypeSystemKey;

  const typeQuery = useProductPreferencesPage_ProductTypesQuery({
    client,
    variables: {
      productTypeKeys: [productTypeKey],
      productTypeSystemKey: resolvedProductTypeSystemKey,
      limit: 1,
    },
    skip: productTypeKey === "",
  });
  const productType =
    getDataOrNull(typeQuery.data?.pimSearchProductTypes)?.productTypes.find(
      type => type.key === productTypeKey
    ) ?? null;

  // Addressed by keys, as stored: a preference under a superseded system has no id.
  const storedProductType = React.useMemo(
    () => ({
      productTypeKey,
      productTypeSystemKey: resolvedProductTypeSystemKey ?? "",
    }),
    [productTypeKey, resolvedProductTypeSystemKey]
  );
  const target = React.useMemo(
    () => ({
      productType: {
        productTypeKey,
        productTypeSystemKey: resolvedProductTypeSystemKey,
      },
    }),
    [productTypeKey, resolvedProductTypeSystemKey]
  );

  const preferences = useProductPreferences(
    productTypeKey === "" ? null : target.productType
  );

  // Inert: PIM looks preferences up by the current system's key, so a preference stored under
  // a superseded system ranks nothing. Its items stay listed and removable — an entry a user
  // cannot see is an entry they cannot remove — but nothing more may be added, moved or edited.
  const isInert =
    productTypeSystemKey !== null &&
    currentProductTypeSystemKey !== null &&
    productTypeSystemKey !== currentProductTypeSystemKey;

  const label = productType?.label ?? productTypeKey;

  const { hasOrganisationPermission } = useUserData();
  const canWriteOrganisationScope = hasOrganisationPermission(
    "pim_catalogue_update"
  );

  const removeArticle = React.useCallback(
    async (
      product: ProductSearchItem__ProductSearchResultFragment,
      scope: PimProductPreferenceScope
    ) => {
      try {
        await preferences.deleteProductPreferences({
          variables: {
            input: {
              scope,
              productType: target.productType,
              articleNumberIdentifiers: [
                {
                  supplierId: product.supplierId,
                  articleNumber: product.articleNumber,
                },
              ],
            },
          },
        });
      } catch (error) {
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(t("Preference removed.", { ns: "Product" }), {
        variant: "success",
      });
    },
    [preferences, target, t]
  );

  const renderArticleAction = React.useCallback(
    (
      product: ProductSearchItem__ProductSearchResultFragment,
      item: { scope: PimProductPreferenceScope }
    ) => {
      // Until the lookup has answered, "no product type" is not a statement about PIM.
      if (productType === null && (typeQuery.loading || typeQuery.error)) {
        return null;
      }
      // Under a superseded product type system there is no id to write against, so the item
      // offers removal only, at the scope it is stored at.
      if (isInert || productType === null) {
        const removeLabel = t("Remove preference", { ns: "Product" });
        const mayRemove =
          item.scope !== "ORGANISATION" || canWriteOrganisationScope;
        return (
          <ButtonCircleWrapper>
            <Tooltip
              title={
                mayRemove
                  ? removeLabel
                  : t(
                      "You need permission to manage the product catalogue to change your organisation's preferences.",
                      { ns: "Product" }
                    )
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={removeLabel}
                  disabled={preferences.writing || !mayRemove}
                  onClick={event => {
                    event.stopPropagation();
                    removeArticle(product, item.scope);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonCircleWrapper>
        );
      }

      return (
        <ButtonCircleWrapper>
          <ProductPreferenceButton
            productTypeKey={productType.key}
            productTypeSystemKey={resolvedProductTypeSystemKey ?? undefined}
            product={product}
            preferences={preferences}
          />
        </ButtonCircleWrapper>
      );
    },
    [
      isInert,
      canWriteOrganisationScope,
      productType,
      typeQuery.loading,
      typeQuery.error,
      resolvedProductTypeSystemKey,
      preferences,
      removeArticle,
      t,
    ]
  );

  const title = (
    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
      <Typography variant="h2">{label}</Typography>
      {/* The key stays next to a resolved title: it is what a preference is stored under. */}
      <Typography variant="body2" sx={{ color: "grey.600" }}>
        {productTypeKey}
      </Typography>
    </Stack>
  );

  if (typeQuery.error)
    return (
      <Stack direction="column" spacing={2} sx={{ minHeight: 0, flex: 1 }}>
        {title}
        <ErrorMessage message={typeQuery.error.message} />
      </Stack>
    );

  if (mode === "assign" && productType !== null)
    return (
      <Stack direction="column" spacing={2} sx={{ minHeight: 0, flex: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          {title}
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => setMode("preferences")}
          >
            {t("Preferred products", { ns: "Product" })}
          </Button>
        </Stack>
        <ProductTypeAssignmentSearch
          productTypeKey={productType.key}
          productTypeSystemKey={resolvedProductTypeSystemKey ?? undefined}
          productTypeLabel={label}
          preferences={preferences}
        />
      </Stack>
    );

  return (
    <RankedPreferenceList
      target={target}
      articleProductType={storedProductType}
      title={title}
      notice={
        isInert ? (
          <InfoMessage
            message={t(
              "These preferences are stored under the {productTypeSystem} product type system, which is no longer in use. They do not affect the order of search results. Remove them if you no longer need them.",
              { ns: "Product", productTypeSystem: productTypeSystemKey }
            )}
          />
        ) : null
      }
      onAssignProducts={
        !isInert && productType !== null
          ? () => {
              // Coming back from the search, the assigned articles are what to look at; the
              // search assigns for the user alone.
              tabs.onChange("ARTICLES");
              scope.onChange("USER");
              setMode("assign");
            }
          : null
      }
      search={
        !isInert && productType !== null
          ? { productTypeKey: productType.key }
          : null
      }
      tabs={tabs}
      scope={scope}
      renderArticleAction={renderArticleAction}
      brandAvailableIn={
        !isInert && productType !== null
          ? { productTypeKey: productType.key }
          : null
      }
      // A product type key PIM does not resolve has no products, so none of its lines is available.
      brandLinesAvailableIn={
        productType !== null
          ? { productTypeKey: productType.key }
          : typeQuery.loading
            ? null
            : NOWHERE
      }
      inert={isInert}
      brandPreferences={brandPreferences}
    />
  );
}

const GLOBAL_TARGET = { global: true } as const;

const NOWHERE = { nowhere: true } as const;

const BOTH_KINDS: readonly PreferenceKind[] = ["ARTICLES", "BRANDS"];

const GLOBAL_SEARCH = { productTypeKey: null };

// Article preferences exist only per product type; the one tab keeps the screen's shape.
const GLOBAL_TABS = {
  kind: "BRANDS",
  kinds: ["BRANDS"],
  onChange: () => undefined,
} as const satisfies {
  kind: PreferenceKind;
  kinds: readonly PreferenceKind[];
  onChange: (kind: PreferenceKind) => void;
};

/** The shown scope, in the URL like the tab, so a reload or a shared link reopens it. */
function usePreferenceScopeParam() {
  const [scopeParam, setScopeParam] = useStateWithUrlParam<string>(
    "scope",
    "mine"
  );
  const value: PimProductPreferenceScope =
    scopeParam === "organisation" ? "ORGANISATION" : "USER";
  return React.useMemo(
    () => ({
      value,
      onChange: (next: PimProductPreferenceScope) =>
        setScopeParam(next === "ORGANISATION" ? "organisation" : "mine"),
    }),
    [value, setScopeParam]
  );
}

/**
 * The Global entry: the global brand preferences. Article preferences exist only per product
 * type, so there are no article items and no assignment here.
 */
export function GlobalBrandPreferences({
  brandPreferences,
}: BrandPreferencesProps) {
  const { t } = useTranslate(["Product"]);
  const scope = usePreferenceScopeParam();

  return (
    <RankedPreferenceList
      target={GLOBAL_TARGET}
      articleProductType={null}
      title={
        <Typography variant="h2">
          {t("Global preferences", { ns: "Product" })}
        </Typography>
      }
      onAssignProducts={null}
      search={GLOBAL_SEARCH}
      tabs={GLOBAL_TABS}
      scope={scope}
      renderArticleAction={renderNoArticleAction}
      brandAvailableIn={GLOBAL_TARGET}
      brandLinesAvailableIn={GLOBAL_TARGET}
      inert={false}
      brandPreferences={brandPreferences}
    />
  );
}

const renderNoArticleAction = () => null;
